import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logoutHandler } from "../../../../store/actions/sessionsActions";
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  IconButton,
  Badge,
  Avatar,
  Menu,
  MenuItem,
  Breadcrumbs,
  Link,
} from "@mui/material";
import { Notifications, Settings, Home } from "@mui/icons-material";

const TOP_NAV_HEIGHT = 64;

const PpTopNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const currentUser = useSelector((state) => state[currentSession]?.user);
  const ppState = useSelector((state) => state[currentSession]?.pp);

  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);

  const unreadNotifications = ppState?.ppNotifications?.unreadCount || 0;

  // Generate breadcrumbs from current path
  const generateBreadcrumbs = () => {
    const pathSegments = location.pathname.split("/").filter(Boolean);
    const breadcrumbs = [
      { label: "Dashboard", path: "/pp", icon: <Home fontSize="small" /> },
    ];

    if (pathSegments.length > 1) {
      // Map path segments to readable names
      const segmentMap = {
        pp: "Private Provider",
        submissions: "Submissions",
        inspections: "Inspections",
        workbench: "Review Workbench",
        photos: "Photo Gallery",
        finance: "Finance",
        payouts: "Payouts",
        statements: "Statements",
        notifications: "Notifications",
        messaging: "Messaging",
        settings: "Settings",
        templates: "Templates",
        new: "New Submission",
        edit: "Edit",
      };

      // Special handling for workbench - always show submissions context
      if (pathSegments.includes("workbench")) {
        breadcrumbs.push({
          label: "Submissions",
          path: "/pp/submissions",
        });
      }

      // Special handling for photos - show workbench context
      if (pathSegments.includes("photos")) {
        breadcrumbs.push({
          label: "Submissions",
          path: "/pp/submissions",
        });
        breadcrumbs.push({
          label: "Review Workbench",
          path: "/pp/workbench/PRS001",
        });
      }

      let currentPath = "";
      pathSegments.forEach((segment, index) => {
        currentPath += `/${segment}`;

        if (segment !== "pp" && segmentMap[segment]) {
          breadcrumbs.push({
            label: segmentMap[segment],
            path: currentPath,
          });
        }
      });
    }

    return breadcrumbs;
  };

  const breadcrumbs = generateBreadcrumbs();

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationMenuOpen = (event) => {
    setNotificationAnchorEl(event.currentTarget);
  };

  const handleNotificationMenuClose = () => {
    setNotificationAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      handleProfileMenuClose();
      navigate("/login");
      await dispatch(logoutHandler(currentSession));
    } catch (error) {
      console.error("Logout error:", error);
      // Still navigate to login even if logout fails
      navigate("/login");
    }
  };

  return (
    <>
      <AppBar
        position="static"
        sx={{
          backdropFilter: "blur(6px)",
        }}
      >
        <Toolbar
          sx={{
            minHeight: TOP_NAV_HEIGHT,
            px: 2,
          }}
        >
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Private Provider Portal
          </Typography>

          {/* Notifications */}
          <IconButton
            size="large"
            color="inherit"
            onClick={handleNotificationMenuOpen}
          >
            <Badge badgeContent={unreadNotifications} color="error">
              <Notifications />
            </Badge>
          </IconButton>

          {/* Settings */}
          <IconButton
            size="large"
            color="inherit"
            onClick={() => navigate("/pp/settings")}
          >
            <Settings />
          </IconButton>

          {/* Profile Menu */}
          <IconButton
            size="large"
            edge="end"
            color="inherit"
            onClick={handleProfileMenuOpen}
          >
            <Avatar sx={{ width: 32, height: 32, bgcolor: "primary.dark" }}>
              {currentUser?.user?.firstName?.[0] || "P"}
            </Avatar>
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Breadcrumbs */}
      <Box
        sx={{
          px: 3,
          py: 1,
          backgroundColor: "grey.50",
          borderBottom: "2px solid #e0e0e0",
          boxShadow: "0 2px 4px rgba(0, 0, 0, 0.5)",
        }}
      >
        <Breadcrumbs aria-label="breadcrumb">
          {breadcrumbs.map((crumb, index) =>
            index === breadcrumbs.length - 1 ? (
              <Box
                key={crumb.path}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  color: "text.primary",
                }}
              >
                {crumb.icon && <Box sx={{ mr: 0.5 }}>{crumb.icon}</Box>}
                <Typography component="span" color="text.primary">
                  {crumb.label}
                </Typography>
              </Box>
            ) : (
              <Link
                key={crumb.path}
                underline="hover"
                color="inherit"
                href={crumb.path}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(crumb.path);
                }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  "&:hover": { color: "primary.main" },
                }}
              >
                {crumb.icon && <Box sx={{ mr: 0.5 }}>{crumb.icon}</Box>}
                <Typography component="span" color="inherit">
                  {crumb.label}
                </Typography>
              </Link>
            )
          )}
        </Breadcrumbs>
      </Box>

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem
          onClick={() => {
            navigate("/pp/settings");
            handleProfileMenuClose();
          }}
        >
          Settings
        </MenuItem>
        <MenuItem
          onClick={() => {
            navigate("/profilesettings");
            handleProfileMenuClose();
          }}
        >
          Profile
        </MenuItem>
        <MenuItem onClick={handleLogout}>Logout</MenuItem>
      </Menu>

      {/* Notification Menu */}
      <Menu
        anchorEl={notificationAnchorEl}
        open={Boolean(notificationAnchorEl)}
        onClose={handleNotificationMenuClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        PaperProps={{
          sx: { width: 320, maxHeight: 400 },
        }}
      >
        <MenuItem
          onClick={() => {
            navigate("/pp/notifications");
            handleNotificationMenuClose();
          }}
        >
          <Typography variant="body2" color="primary">
            View All Notifications ({unreadNotifications} unread)
          </Typography>
        </MenuItem>
        {ppState?.ppNotifications?.data?.slice(0, 3).map((notification) => (
          <MenuItem key={notification.id} onClick={handleNotificationMenuClose}>
            <Box>
              <Typography
                variant="body2"
                fontWeight={notification.read ? "normal" : "bold"}
              >
                {notification.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {notification.message.length > 50
                  ? `${notification.message.substring(0, 50)}...`
                  : notification.message}
              </Typography>
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default PpTopNav;
